import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { foreshadowApi } from '@/api/foreshadowApi'
import { friendlyMessage } from '@/api/http'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

export interface DebtContract {
 pending_decisions?: {id: string; title: string; detail: string; hint: string}[]
 new_plant_limit: number
 reschedule_chapter?: number
 suggestions?: string[]
 must_resolve: {id: string; title: string; detail: string; hint: string}[]
}
export function ForeshadowDebtContract({projectId, branchId = 'main', chapterNo, contract, onChanged}: {
 projectId: string; branchId?: string; chapterNo: number; contract: DebtContract; onChanged: () => Promise<void>
}) {
 const {t} = useTranslation()
 const [targets, setTargets] = useState<Record<string,string>>({})
 const [busy, setBusy] = useState(false)
 const [error, setError] = useState('')
 async function rescheduleAll() {
  setBusy(true); setError('')
  try {
   await foreshadowApi.reschedulePending(projectId, chapterNo, branchId)
   await onChanged()
  } catch (err) { setError(friendlyMessage(err, t('foreshadowDebt.failed'))) }
  finally { setBusy(false) }
 }
 async function decide(id: string, action: 'reschedule' | 'long' | 'delete') {
  setBusy(true); setError('')
  try {
   if (action === 'delete') await foreshadowApi.remove(projectId,id,branchId)
   else await foreshadowApi.decide(projectId,id,branchId,{target_resolve_chapter_number: Number(targets[id]), is_long_term: action === 'long'})
   await onChanged()
  } catch (err) {setError(friendlyMessage(err,t('foreshadowDebt.failed')))} finally {setBusy(false)}
 }
 return <section className="space-y-3 border-t pt-4" aria-label={t('foreshadowDebt.title')}>
  <h3 className="font-medium">{t('foreshadowDebt.title')}</h3>
  <p className="text-sm text-muted-foreground">{t('foreshadowDebt.limit',{count:contract.new_plant_limit})}</p>
  {contract.must_resolve.length === 0 && !contract.pending_decisions?.length && <p className="text-sm text-muted-foreground">{t('foreshadowDebt.empty')}</p>}
  {[{pending:false, items:contract.must_resolve}, {pending:true, items:contract.pending_decisions ?? []}].map(group => <div key={String(group.pending)} className="space-y-3">
  {group.pending && group.items.length > 0 && <div className="space-y-2 border-t pt-3">
   <h4 className="font-medium">{t('foreshadowDebt.pending', {count:group.items.length})}</h4>
   <p className="text-sm text-muted-foreground">{t('foreshadowDebt.suggestion')}</p>
   <Button type="button" variant="outline" className="h-auto whitespace-normal" disabled={busy || !contract.reschedule_chapter || contract.reschedule_chapter <= chapterNo} onClick={()=>void rescheduleAll()}>{t('foreshadowDebt.rescheduleAll')}</Button>
   {(!contract.reschedule_chapter || contract.reschedule_chapter <= chapterNo) && <p className="text-sm text-muted-foreground">{t('foreshadowDebt.noFutureVolumeEnd')}</p>}
  </div>}
  {group.items.map(f => {
   const target = Number(targets[f.id])
   const valid = Number.isInteger(target) && target > chapterNo
   return <div id={`foreshadow-debt-${f.id}`} key={f.id} className="space-y-2 break-words">
    <p className="font-medium">{f.title}</p><p className="text-sm">{f.detail}</p>
    {f.hint && <blockquote className="text-sm text-muted-foreground">{f.hint}</blockquote>}
    <div className="flex flex-wrap items-center gap-2">
     <Input type="number" min={chapterNo+1} className="w-28" aria-label={t('foreshadowDebt.target')} value={targets[f.id] ?? ''} onChange={e=>setTargets({...targets,[f.id]:e.target.value})} />
     <Button type="button" variant="outline" disabled={busy || !valid} onClick={()=>void decide(f.id,'reschedule')}>{t('foreshadowDebt.reschedule')}</Button>
     <Button type="button" variant="outline" disabled={busy || !valid} onClick={()=>void decide(f.id,'long')}>{t('foreshadowDebt.long')}</Button>
     <Button type="button" variant="ghost" disabled={busy} onClick={()=>void decide(f.id,'delete')}>{t('foreshadowDebt.delete')}</Button>
    </div>
   </div>
  })}</div>)}
  {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
 </section>
}
